import i18next from 'i18next';

export function test() {
	return null;
}

export function getOS() {
	const { userAgent } = window.navigator;
	const { platform } = window.navigator;
	const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
	const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
	const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
	let os = null;

	if (macosPlatforms.indexOf(platform) !== -1) {
		os = 'MacOS';
	} else if (iosPlatforms.indexOf(platform) !== -1) {
		os = 'iOS';
	} else if (windowsPlatforms.indexOf(platform) !== -1) {
		os = 'Windows';
	} else if (/Android/.test(userAgent)) {
		os = 'Android';
	} else if (!os && /Linux/.test(platform)) {
		os = 'Linux';
	}

	// @ts-ignore
	document.documentElement.setAttribute('os', os);
	return os;
}

export const hasNotch = () => {
	/**
	 * For storybook test
	 */
	const storybook = window.location !== window.parent.location;
	// @ts-ignore
	const iPhone = /iPhone/.test(navigator.userAgent) && !window.MSStream;
	const aspect = window.screen.width / window.screen.height;
	const aspectFrame = window.innerWidth / window.innerHeight;
	return (
		(iPhone && aspect.toFixed(3) === '0.462') ||
		(storybook && aspectFrame.toFixed(3) === '0.462')
	);
};

export const mergeRefs = (refs: any[]) => {
	return (value: any) => {
		refs.forEach((ref) => {
			if (typeof ref === 'function') {
				ref(value);
			} else if (ref != null) {
				ref.current = value;
			}
		});
	};
};

export const randomColor = () => {
	const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger'];

	const color = Math.floor(Math.random() * colors.length);

	return colors[color];
};

/** The store's one currency — must match backend/src/config/currency.js. */
export const CURRENCY = 'EUR';

// Site language → number format ("16,00 €" in Greek, "€16.00" in English).
const PRICE_LOCALES: Record<string, string> = {
	en: 'en-IE', gr: 'el-GR', el: 'el-GR', ar: 'ar-u-nu-latn', tr: 'tr-TR', fr: 'fr-FR', de: 'de-DE',
};

/** Price in major units (euros) → localized string, e.g. 16 → "16,00 €". */
export const priceFormat = (price: number) => {
	const lang = (i18next.language || 'en').split('-')[0];
	return price.toLocaleString(PRICE_LOCALES[lang] || 'en-IE', {
		style: 'currency',
		currency: CURRENCY,
	});
};

/**
 * Convert a price from cents to major units (euros).
 * All backend prices are now in cents.
 */
export const normalizePrice = (priceInCents: number): number => {
	return priceInCents / 100;
};

/**
 * Format a price (in cents) for display as currency.
 */
export const displayPrice = (priceInCents: number): string => {
	return priceFormat(normalizePrice(priceInCents));
};

/**
 * Format a recommended-age band code (e.g. "3-5" or the open-ended "9+") for
 * display, using an en-dash for closed ranges. Returns an empty string for
 * missing/unknown values so callers can hide the tag.
 */
export const formatAgeRange = (ageGroup?: string): string => {
	if (!ageGroup) return '';
	const trimmed = ageGroup.trim();
	if (/^\d+\+$/.test(trimmed)) return trimmed;
	const match = /^(\d+)-(\d+)$/.exec(trimmed);
	return match ? `${match[1]}–${match[2]}` : '';
};

/**
 * Default customization age for a book, taken as the midpoint of its
 * recommended age band (e.g. "3-5" → 4, rounded down for odd spans). The
 * open-ended "9+" band defaults to its floor. Falls back to 3 when the
 * product has no (or an unparsable) age group.
 */
export const defaultAgeForGroup = (ageGroup?: string): number => {
	const trimmed = ageGroup?.trim() ?? '';
	const openEnded = /^(\d+)\+$/.exec(trimmed);
	if (openEnded) return Number(openEnded[1]);
	const match = /^(\d+)-(\d+)$/.exec(trimmed);
	if (match) return Math.floor((Number(match[1]) + Number(match[2])) / 2);
	return 3;
};

/**
 * Convert the stored scene/spread count into the physical page count shown to
 * customers. In the DB, a book's `pages` field is the number of scenes:
 *   - a storybook scene is a 2-page spread (illustration page + text page)
 *     → physical pages = scenes × 2
 *   - a coloring-book scene is a single printed page → physical pages = scenes
 * Non-book types pass through unchanged. Returns undefined for missing counts so
 * callers can hide the meta line.
 */
export const bookPageCount = (scenes?: number, productType?: string): number | undefined => {
	if (!scenes || scenes <= 0) return undefined;
	return productType === 'storybook' ? scenes * 2 : scenes;
};

export const average = (array: any[]) => array.reduce((a, b) => a + b) / array.length;

export const percent = (value1: number, value2: number) =>
	Number(((value1 / value2 - 1) * 100).toFixed(2));

export const getFirstLetter = (text: string, letterCount = 2): string =>
	// @ts-ignore
	text
		.toUpperCase()
		.match(/\b(\w)/g)
		.join('')
		.substring(0, letterCount);

export const debounce = (func: (arg0: any) => void, wait = 1000) => {
	let timeout: string | number | NodeJS.Timeout | undefined;

	return function executedFunction(...args: any[]) {
		const later = () => {
			clearTimeout(timeout);
			// @ts-ignore
			func(...args);
		};

		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

/**
 * Child names per language used to replace the {X} placeholder
 * in product titles and descriptions for display purposes.
 */
const HERO_NAMES: Record<string, string[]> = {
	en: ['Emma', 'Liam', 'Olivia', 'Noah', 'Sophia', 'Lucas', 'Mia', 'Leo', 'Aria', 'Ethan'],
	ar: ['ريم', 'يوسف', 'ليلى', 'آدم', 'نور', 'عمر', 'سارة', 'زيد', 'جنى', 'كريم'],
	gr: ['Ελένη', 'Γιάννης', 'Μαρία', 'Νίκος', 'Σοφία', 'Δημήτρης', 'Αθηνά', 'Κώστας', 'Ζωή', 'Αλέξης'],
	de: ['Emma', 'Ben', 'Mia', 'Leon', 'Hannah', 'Finn', 'Lina', 'Paul', 'Ella', 'Felix'],
	fr: ['Léa', 'Hugo', 'Chloé', 'Lucas', 'Emma', 'Louis', 'Manon', 'Raphaël', 'Jade', 'Arthur'],
	tr: ['Elif', 'Yusuf', 'Zeynep', 'Arda', 'Defne', 'Emir', 'Ecrin', 'Kerem', 'Asya', 'Baran'],
};

/**
 * Simple numeric hash from a string, used to deterministically
 * pick the same hero name for the same product every time.
 */
const hashCode = (str: string): number => {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
	}
	return Math.abs(hash);
};

/**
 * Replace every `{X}` in a text string with a consistent random child
 * name derived from the product ID and current language.
 *
 * @param text       The raw title or description from the API.
 * @param productId  Used as seed so the same product always gets the same name.
 * @param language   Current i18n language code (e.g. "en", "ar", "gr").
 * @param heroName     Optional real hero name — used on customization / cart pages
 *                     when the user has already entered a name.
 * @param displayTitle Optional admin-authored display title (no {X}, gender/grammar
 *                     already correct for the current language) — preferred over the
 *                     random placeholder-name substitution when no real name is known yet.
 */
export const resolveHeroName = (
	text: string | undefined,
	productId: string,
	language: string,
	heroName?: string,
	displayTitle?: string,
): string => {
	if (!text || !text.includes('{X}')) return text ?? '';
	if (heroName) return text.replaceAll('{X}', heroName);
	if (displayTitle) return displayTitle;
	const lang = language.split('-')[0].toLowerCase();
	const names = HERO_NAMES[lang] || HERO_NAMES.en;
	const name = names[hashCode(productId) % names.length];
	return text.replaceAll('{X}', name);
};

